import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import ReactPaginate from 'react-paginate';
import * as XLSX from 'xlsx';
import { fetchOrdenesEncabezadoParams, fetchPublicacionAuditoriaPrecioStockApi, fetchSellerAuditoriaApi } from '../../../../api/axios';
import { getAuditoriaTableId } from '../../../../helper/frontendConfigHelper';
import { selectSellerMerged } from '../../../../helper/sellerMergedSelector';
import './AuditoriaPrecioStock.css';

const buildEmptyResponse = () => ({
    resumen: {
        precioListaActual: null,
        precioPromocionalActual: null,
        stockActual: null,
        ventas90Dias: 0,
        cantidadAlertas: 0
    },
    periodos: []
});

const toArray = (value, alternate) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(alternate)) return alternate;
    return [];
};

const firstArray = (...values) => {
    let fallbackEmptyArray = null;

    for (const value of values) {
        if (Array.isArray(value)) {
            if (value.length > 0) return value;
            fallbackEmptyArray = fallbackEmptyArray ?? value;
            continue;
        }
        if (Array.isArray(value?.content)) {
            if (value.content.length > 0) return value.content;
            fallbackEmptyArray = fallbackEmptyArray ?? value.content;
            continue;
        }
        if (Array.isArray(value?.Content)) {
            if (value.Content.length > 0) return value.Content;
            fallbackEmptyArray = fallbackEmptyArray ?? value.Content;
            continue;
        }
        if (typeof value === 'string' && value.trim()) {
            const trimmed = value.trim();
            if (trimmed.startsWith('[')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed)) {
                        if (parsed.length > 0) return parsed;
                        fallbackEmptyArray = fallbackEmptyArray ?? parsed;
                        continue;
                    }
                } catch {
                    continue;
                }
            }
            const valuesFromString = trimmed.split(',').map((item) => item.trim()).filter(Boolean);
            if (valuesFromString.length > 0) return valuesFromString;
        }
    }
    return fallbackEmptyArray ?? [];
};

const normalizeOrden = (orden, options = {}) => {
    const { allowGenericFallback = true } = options;

    if (orden == null || orden === '') return null;
    if (typeof orden === 'number' || typeof orden === 'string') return String(orden);

    const value = orden?.idOrden
        ?? orden?.IdOrden
        ?? orden?.idOrdenVenta
        ?? orden?.IdOrdenVenta
        ?? orden?.idVenta
        ?? orden?.IdVenta
        ?? orden?.ventaId
        ?? orden?.VentaId
        ?? orden?.idOrdenMarketplace
        ?? orden?.IdOrdenMarketplace
        ?? orden?.ordenId
        ?? orden?.OrdenId
        ?? orden?.numeroOrden
        ?? orden?.NumeroOrden
        ?? orden?.numeroVenta
        ?? orden?.NumeroVenta
        ?? (allowGenericFallback ? orden?.id : null)
        ?? (allowGenericFallback ? orden?.Id : null)
        ?? (allowGenericFallback ? orden?.codigo : null)
        ?? (allowGenericFallback ? orden?.Codigo : null);

    return value == null || value === '' ? null : String(value);
};

const ORDER_COLLECTION_KEY_PATTERN = /(ids?orden(?:es)?|ids?ventas?|ordenes?incluidas?|ventas?incluidas?|detalleordenes|detalleventas|ordenes?detalle|ventas?detalle|ordenes?|ventas?)/i;
const ORDER_IDENTIFIER_KEY_PATTERN = /(idorden|idordenventa|idordenmarketplace|ordenoid|numeroorden|numeroventa|idventa|ventaid|idsordenes|idsventas)/i;

const uniqueValues = (values = []) => Array.from(new Set(values.filter(Boolean)));

const uniqueOrders = (orders = []) => Array.from(
    orders.reduce((acc, order, index) => {
        const key = normalizeOrden(order) ?? `${order?.fecha ?? order?.Fecha ?? 'orden'}-${index}`;
        if (!acc.has(key)) {
            acc.set(key, order);
        }
        return acc;
    }, new Map()).values()
);

const toComparableText = (value) => String(value ?? '').trim().toLowerCase();

const toDateFilterValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && value.length >= 10) return value.slice(0, 10);

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 10);
};

const toComparableDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const extractOrderIdsFromUnknownShape = (value, key = '', depth = 0) => {
    if (value == null || depth > 6) return [];

    const normalizedKey = String(key || '');
    const isCollectionContext = ORDER_COLLECTION_KEY_PATTERN.test(normalizedKey);
    const isIdentifierKey = ORDER_IDENTIFIER_KEY_PATTERN.test(normalizedKey);
    const isIdListKey = /ids?orden(?:es)?|ids?ventas?/i.test(normalizedKey);

    if (Array.isArray(value)) {
        if (isIdListKey) {
            return uniqueValues(value.map((item) => normalizeOrden(item, { allowGenericFallback: false })));
        }

        return uniqueValues(value.flatMap((item) => {
            const directId = isCollectionContext
                ? normalizeOrden(item, { allowGenericFallback: true })
                : null;
            return [
                directId,
                ...extractOrderIdsFromUnknownShape(item, normalizedKey, depth + 1)
            ];
        }));
    }

    if (typeof value === 'string') {
        if (!isIdentifierKey && !isIdListKey) return [];
        return uniqueValues(firstArray(value).map((item) => normalizeOrden(item, { allowGenericFallback: false })));
    }

    if (typeof value === 'number') {
        if (!isIdentifierKey || !Number.isFinite(value) || value <= 0) return [];
        return [String(value)];
    }

    if (typeof value === 'object') {
        const directId = isCollectionContext
            ? normalizeOrden(value, { allowGenericFallback: true })
            : null;

        return uniqueValues([
            directId,
            ...Object.entries(value).flatMap(([nestedKey, nestedValue]) => (
                extractOrderIdsFromUnknownShape(nestedValue, nestedKey, depth + 1)
            ))
        ]);
    }

    return [];
};

const normalizeOrdenesPeriodo = (periodo) => {
    const directIds = firstArray(
        periodo?.idsOrdenes,
        periodo?.IdsOrdenes,
        periodo?.idOrdenes,
        periodo?.IdOrdenes,
        periodo?.idsOrdenesIncluidas,
        periodo?.IdsOrdenesIncluidas,
        periodo?.ordenesIncluidas,
        periodo?.OrdenesIncluidas,
        periodo?.ventasIncluidas,
        periodo?.VentasIncluidas,
        periodo?.ordenes,
        periodo?.Ordenes,
        periodo?.ventas,
        periodo?.Ventas,
        periodo?.idsVentas,
        periodo?.IdsVentas,
        periodo?.idVentas,
        periodo?.IdVentas
    ).map((orden) => normalizeOrden(orden)).filter(Boolean);

    if (directIds.length) {
        return uniqueValues(directIds);
    }

    const nestedIds = [
        ...extractOrderIdsFromUnknownShape(periodo?.detalleOrdenes, 'detalleOrdenes'),
        ...extractOrderIdsFromUnknownShape(periodo?.DetalleOrdenes, 'DetalleOrdenes'),
        ...extractOrderIdsFromUnknownShape(periodo?.detalleVentas, 'detalleVentas'),
        ...extractOrderIdsFromUnknownShape(periodo?.DetalleVentas, 'DetalleVentas'),
        ...extractOrderIdsFromUnknownShape(periodo?.ordenesDetalle, 'ordenesDetalle'),
        ...extractOrderIdsFromUnknownShape(periodo?.OrdenesDetalle, 'OrdenesDetalle'),
        ...extractOrderIdsFromUnknownShape(periodo?.ventasDetalle, 'ventasDetalle'),
        ...extractOrderIdsFromUnknownShape(periodo?.VentasDetalle, 'VentasDetalle')
    ];

    return uniqueValues([...directIds, ...nestedIds]);
};

const orderMatchesPublication = (orden, targetSkuMarket) => {
    const sku = toComparableText(targetSkuMarket);
    if (!sku) return true;

    const productos = toArray(orden?.productos, orden?.Productos);
    if (!productos.length) return true;

    return productos.some((producto) => (
        toComparableText(producto?.skuMarket ?? producto?.SkuMarket ?? producto?.codProducto ?? producto?.CodProducto) === sku
    ));
};

const orderFallsWithinPeriodo = (orden, periodo) => {
    const orderDate = toComparableDate(orden?.fecha ?? orden?.Fecha);
    const periodStart = toComparableDate(periodo?.fechaDesde);
    const periodEnd = toComparableDate(periodo?.fechaHasta);

    if (orderDate == null || periodStart == null || periodEnd == null) return true;
    return orderDate >= periodStart && orderDate <= periodEnd;
};

const extractOrderIdsFromOrdersResponse = (response, periodo, targetSkuMarket) => (
    uniqueValues(
        toArray(response?.elementos)
            .filter((orden) => orderMatchesPublication(orden, targetSkuMarket))
            .filter((orden) => orderFallsWithinPeriodo(orden, periodo))
            .map((orden) => normalizeOrden(orden))
    )
);

const buildPeriodosToResolve = (periodos, fetchStatusMap, skuMarket) => (
    periodos.filter((periodo) => (
        periodo?.cantidadVentas > 0
        && !periodo?.idsOrdenes?.length
        && !fetchStatusMap?.[periodo.id]
        && periodo?.fechaDesde
        && periodo?.fechaHasta
        && skuMarket
    ))
);

const fetchOrderIdsForPeriodo = async ({ periodo, skuMarket, sellerId }) => {
    try {
        const cantidadEsperada = Math.max(periodo?.cantidadVentas || 0, 1);
        const responseOrdenes = await fetchOrdenesEncabezadoParams({
            numeroPagina: 1,
            cantidadPagina: Math.min(Math.max(cantidadEsperada * 3, 20), 200),
            fechaDesde: toDateFilterValue(periodo?.fechaDesde),
            fechaHasta: toDateFilterValue(periodo?.fechaHasta),
            skuMarket,
            idSeller: sellerId
        });

        return extractOrderIdsFromOrdersResponse(responseOrdenes, periodo, skuMarket);
    } catch {
        return [];
    }
};

const buildOrderLookupRange = (periodos = []) => {
    const fechasDesde = periodos
        .map((periodo) => toDateFilterValue(periodo?.fechaDesde))
        .filter(Boolean)
        .sort();
    const fechasHasta = periodos
        .map((periodo) => toDateFilterValue(periodo?.fechaHasta))
        .filter(Boolean)
        .sort();

    if (!fechasDesde.length || !fechasHasta.length) return null;

    return {
        fechaDesde: fechasDesde[0],
        fechaHasta: fechasHasta[fechasHasta.length - 1]
    };
};

const fetchOrdersForPeriodos = async ({ periodos, skuMarket, sellerId }) => {
    const range = buildOrderLookupRange(periodos);
    if (!range || !skuMarket) return [];

    const totalEsperado = periodos.reduce(
        (acc, periodo) => acc + Math.max(toNumberOrNull(periodo?.cantidadVentas) ?? 0, 0),
        0
    );
    const cantidadPagina = Math.min(Math.max(totalEsperado * 3, 50), 200);
    const firstResponse = await fetchOrdenesEncabezadoParams({
        numeroPagina: 1,
        cantidadPagina,
        fechaDesde: range.fechaDesde,
        fechaHasta: range.fechaHasta,
        skuMarket,
        idSeller: sellerId
    });

    const firstElements = toArray(firstResponse?.elementos);
    const totalPages = Math.max(toNumberOrNull(firstResponse?.cantidadPagina) ?? 1, 1);

    if (totalPages <= 1) {
        return firstElements;
    }

    const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
    const remainingResponses = await Promise.all(remainingPages.map((page) => (
        fetchOrdenesEncabezadoParams({
            numeroPagina: page,
            cantidadPagina,
            fechaDesde: range.fechaDesde,
            fechaHasta: range.fechaHasta,
            skuMarket,
            idSeller: sellerId
        })
    )));

    return uniqueOrders([
        ...firstElements,
        ...remainingResponses.flatMap((response) => toArray(response?.elementos))
    ]);
};

const mapOrderIdsByPeriod = (orders = [], periodos = [], skuMarket) => (
    periodos.reduce((acc, periodo) => {
        acc[periodo.id] = uniqueValues(
            orders
                .filter((orden) => orderMatchesPublication(orden, skuMarket))
                .filter((orden) => orderFallsWithinPeriodo(orden, periodo))
                .map((orden) => normalizeOrden(orden))
        );
        return acc;
    }, {})
);

const resolveOrderIdsForPeriodos = async ({ periodos, skuMarket, sellerId }) => {
    if (!periodos.length || !skuMarket) return {};

    if (periodos.length === 1) {
        const periodo = periodos[0];
        return {
            [periodo.id]: await fetchOrderIdsForPeriodo({
                periodo,
                skuMarket,
                sellerId
            })
        };
    }

    try {
        const orders = await fetchOrdersForPeriodos({
            periodos,
            skuMarket,
            sellerId
        });

        return mapOrderIdsByPeriod(orders, periodos, skuMarket);
    } catch {
        const results = await Promise.all(periodos.map(async (periodo) => ({
            periodId: periodo.id,
            idsOrdenes: await fetchOrderIdsForPeriodo({
                periodo,
                skuMarket,
                sellerId
            })
        })));

        return results.reduce((acc, result) => ({
            ...acc,
            [result.periodId]: result.idsOrdenes
        }), {});
    }
};

const toNumberOrNull = (value) => {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const toMoneyOrNull = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;

        const sanitized = trimmed
            .replace(/[^\d,.-]/g, '')
            .replace(/\s+/g, '');

        if (!sanitized) return null;

        const sign = sanitized.startsWith('-') ? '-' : '';
        const unsigned = sanitized.replace(/-/g, '');

        if (!unsigned) return null;

        const strategies = [
            [/^\d{1,3}(\.\d{3})+,\d{1,4}$/, (raw) => raw.replace(/\./g, '').replace(',', '.')],
            [/^\d{1,3}(,\d{3})+\.\d{1,4}$/, (raw) => raw.replace(/,/g, '')],
            [/^\d{1,3}(\.\d{3})+$/, (raw) => raw.replace(/\./g, '')],
            [/^\d{1,3}(,\d{3})+$/, (raw) => raw.replace(/,/g, '')],
            [/^\d+,\d{1,4}$/, (raw) => raw.replace(',', '.')],
            [/^\d+\.\d{1,4}$/, (raw) => raw],
            [/^\d+$/, (raw) => raw]
        ];

        let normalized = null;

        for (const [pattern, transform] of strategies) {
            if (pattern.test(unsigned)) {
                normalized = transform(unsigned);
                break;
            }
        }

        if (normalized == null) {
            const lastComma = unsigned.lastIndexOf(',');
            const lastDot = unsigned.lastIndexOf('.');
            const lastSeparatorIndex = Math.max(lastComma, lastDot);

            if (lastSeparatorIndex >= 0) {
                const integerPart = unsigned.slice(0, lastSeparatorIndex).replace(/[.,]/g, '');
                const decimalPart = unsigned.slice(lastSeparatorIndex + 1).replace(/[.,]/g, '');
                normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
            } else {
                normalized = unsigned.replace(/[.,]/g, '');
            }
        }

        const parsed = Number(`${sign}${normalized}`);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const isFinitePositiveMoney = (value) => Number.isFinite(value) && value > 0;

const isPotentialShiftedMoneyValue = (value) => (
    Number.isFinite(value)
    && value > 100000
    && Number.isInteger(value)
);

const toShiftedMoneyCandidate = (value) => Number((value / 100).toFixed(4));

const buildShiftedMoneyCandidates = (value, divisors = [10, 100, 1000]) => (
    divisors
        .map((divisor) => ({
            divisor,
            value: Number((value / divisor).toFixed(4))
        }))
        .filter((candidate) => Number.isFinite(candidate.value) && candidate.value > 0)
);

const getClosestDistance = (value, references = []) => {
    const validReferences = references.filter(isFinitePositiveMoney);
    if (!validReferences.length) return null;
    return Math.min(...validReferences.map((reference) => Math.abs(value - reference)));
};

const computePercentDelta = (before, after) => {
    if (!Number.isFinite(before) || !Number.isFinite(after) || before === 0) return null;
    return ((after - before) / Math.abs(before)) * 100;
};

const computeNumericDelta = (before, after) => {
    if (!Number.isFinite(before) || !Number.isFinite(after)) return null;
    return after - before;
};

const maybeFixShiftedPromotionalValue = (value, listPrice, ...referenceValues) => {
    if (
        !isPotentialShiftedMoneyValue(value)
    ) {
        return value;
    }

    const candidate = toShiftedMoneyCandidate(value);
    const hasListPrice = isFinitePositiveMoney(listPrice);
    const validReferences = referenceValues.flat().filter(isFinitePositiveMoney);

    const rawRatioToList = hasListPrice ? (value / listPrice) : null;
    const candidateRatioToList = hasListPrice ? (candidate / listPrice) : null;
    const rawDistanceToReference = getClosestDistance(value, validReferences);
    const candidateDistanceToReference = getClosestDistance(candidate, validReferences);

    const looksShiftedAgainstList = hasListPrice
        && (
            (rawRatioToList > 20 && candidateRatioToList <= 5)
            || (rawRatioToList > 4 && candidateRatioToList <= 1.25)
        );

    const candidateIsMuchCloserToReference = rawDistanceToReference != null
        && candidateDistanceToReference != null
        && candidateDistanceToReference * 10 <= rawDistanceToReference;

    return (looksShiftedAgainstList || candidateIsMuchCloserToReference) ? candidate : value;
};

const maybeFixShiftedListValue = (value, promoValue, ...referenceValues) => {
    if (!isPotentialShiftedMoneyValue(value)) {
        return value;
    }

    const validReferences = referenceValues.flat().filter(isFinitePositiveMoney);
    const rawDistanceToReference = getClosestDistance(value, validReferences);
    const rawRatioToPromo = isFinitePositiveMoney(promoValue) ? (value / promoValue) : null;
    const candidates = buildShiftedMoneyCandidates(value)
        .map((candidate) => ({
            ...candidate,
            distanceToReference: getClosestDistance(candidate.value, validReferences),
            ratioToPromo: isFinitePositiveMoney(promoValue) ? (candidate.value / promoValue) : null
        }))
        .filter((candidate) => {
            if (!isFinitePositiveMoney(promoValue)) return true;
            return candidate.value >= (promoValue * 0.95) && candidate.ratioToPromo <= 5;
        })
        .sort((left, right) => {
            const leftDistance = left.distanceToReference ?? Number.POSITIVE_INFINITY;
            const rightDistance = right.distanceToReference ?? Number.POSITIVE_INFINITY;

            if (leftDistance !== rightDistance) {
                return leftDistance - rightDistance;
            }

            return right.divisor - left.divisor;
        });

    const bestCandidate = candidates[0];

    if (!bestCandidate) {
        return value;
    }

    const candidateIsMuchCloserToReference = rawDistanceToReference != null
        && bestCandidate.distanceToReference != null
        && bestCandidate.distanceToReference * 8 <= rawDistanceToReference;

    const looksShiftedAgainstPromo = isFinitePositiveMoney(promoValue)
        && rawRatioToPromo > 10
        && bestCandidate.ratioToPromo >= 0.95
        && bestCandidate.ratioToPromo <= 5;

    return (candidateIsMuchCloserToReference || looksShiftedAgainstPromo)
        ? bestCandidate.value
        : value;
};

const normalizeAlert = (alerta) => {
    if (!alerta) return null;
    if (typeof alerta === 'string') {
        return {
            codigo: alerta,
            descripcion: alerta.replace(/_/g, ' ')
        };
    }

    return {
        codigo: alerta?.codigo ?? alerta?.Codigo ?? alerta?.id ?? alerta?.Id ?? 'alerta',
        descripcion: alerta?.descripcion ?? alerta?.Descripcion ?? alerta?.nombre ?? alerta?.Nombre ?? 'Alerta'
    };
};

const normalizeEvento = (evento, index, periodContext = {}) => {
    const antes = evento?.antes ?? evento?.Antes ?? {};
    const despues = evento?.despues ?? evento?.Despues ?? {};
    const delta = evento?.delta ?? evento?.Delta ?? {};

    const antesPrecioLista = toMoneyOrNull(antes?.precioLista ?? antes?.PrecioLista);
    const despuesPrecioLista = toMoneyOrNull(despues?.precioLista ?? despues?.PrecioLista);
    const despuesPrecioPromocionalInicial = toMoneyOrNull(despues?.precioPromocional ?? despues?.PrecioPromocional);
    const antesPrecioPromocionalInicial = toMoneyOrNull(antes?.precioPromocional ?? antes?.PrecioPromocional);
    const despuesPrecioPromocional = maybeFixShiftedPromotionalValue(
        despuesPrecioPromocionalInicial,
        despuesPrecioLista,
        periodContext?.precioPromocional ?? antesPrecioPromocionalInicial
    );
    const antesPrecioPromocional = maybeFixShiftedPromotionalValue(
        antesPrecioPromocionalInicial,
        antesPrecioLista,
        despuesPrecioPromocional ?? periodContext?.precioPromocional
    );
    const antesStock = toNumberOrNull(antes?.stock ?? antes?.Stock);
    const despuesStock = toNumberOrNull(despues?.stock ?? despues?.Stock);

    return {
        id: evento?.id ?? evento?.Id ?? `${evento?.fecha ?? evento?.Fecha ?? 'evento'}-${index}`,
        fecha: evento?.fecha ?? evento?.Fecha ?? null,
        origen: evento?.origen ?? evento?.Origen ?? '-',
        usuario: evento?.usuario ?? evento?.Usuario ?? '-',
        idUsuario: evento?.idUsuario ?? evento?.IdUsuario ?? null,
        antes: {
            precioLista: antesPrecioLista,
            precioPromocional: antesPrecioPromocional,
            stock: antesStock
        },
        despues: {
            precioLista: despuesPrecioLista,
            precioPromocional: despuesPrecioPromocional,
            stock: despuesStock
        },
        delta: {
            precioListaAbs: computeNumericDelta(antesPrecioLista, despuesPrecioLista)
                ?? toMoneyOrNull(delta?.precioListaAbs ?? delta?.PrecioListaAbs ?? delta?.precioLista ?? delta?.PrecioLista),
            precioListaPct: computePercentDelta(antesPrecioLista, despuesPrecioLista)
                ?? toNumberOrNull(delta?.precioListaPct ?? delta?.PrecioListaPct),
            precioPromocionalAbs: computeNumericDelta(antesPrecioPromocional, despuesPrecioPromocional)
                ?? toMoneyOrNull(delta?.precioPromocionalAbs ?? delta?.PrecioPromocionalAbs ?? delta?.precioPromocional ?? delta?.PrecioPromocional),
            precioPromocionalPct: computePercentDelta(antesPrecioPromocional, despuesPrecioPromocional)
                ?? toNumberOrNull(delta?.precioPromocionalPct ?? delta?.PrecioPromocionalPct),
            stockAbs: computeNumericDelta(antesStock, despuesStock)
                ?? toNumberOrNull(delta?.stockAbs ?? delta?.StockAbs ?? delta?.stock ?? delta?.Stock),
            stockPct: computePercentDelta(antesStock, despuesStock)
                ?? toNumberOrNull(delta?.stockPct ?? delta?.StockPct)
        },
        alertas: toArray(evento?.alertas, evento?.Alertas).map(normalizeAlert).filter(Boolean)
    };
};

const stabilizeEventoMoneyValues = (evento, periodContext = {}) => {
    const despuesPrecioPromocional = maybeFixShiftedPromotionalValue(
        evento?.despues?.precioPromocional,
        evento?.despues?.precioLista,
        periodContext?.precioPromocional,
        periodContext?.summaryPrecioPromocional,
        evento?.antes?.precioPromocional
    );

    const antesPrecioPromocional = maybeFixShiftedPromotionalValue(
        evento?.antes?.precioPromocional,
        evento?.antes?.precioLista,
        despuesPrecioPromocional,
        periodContext?.precioPromocional,
        periodContext?.summaryPrecioPromocional
    );

    const despuesPrecioLista = maybeFixShiftedListValue(
        evento?.despues?.precioLista,
        despuesPrecioPromocional,
        periodContext?.precioLista,
        periodContext?.summaryPrecioLista,
        evento?.antes?.precioLista
    );

    const antesPrecioLista = maybeFixShiftedListValue(
        evento?.antes?.precioLista,
        antesPrecioPromocional,
        despuesPrecioLista,
        periodContext?.precioLista,
        periodContext?.summaryPrecioLista
    );

    return {
        ...evento,
        antes: {
            ...evento.antes,
            precioLista: antesPrecioLista,
            precioPromocional: antesPrecioPromocional
        },
        despues: {
            ...evento.despues,
            precioLista: despuesPrecioLista,
            precioPromocional: despuesPrecioPromocional
        },
        delta: {
            ...evento.delta,
            precioListaAbs: computeNumericDelta(antesPrecioLista, despuesPrecioLista) ?? evento?.delta?.precioListaAbs,
            precioListaPct: computePercentDelta(antesPrecioLista, despuesPrecioLista) ?? evento?.delta?.precioListaPct,
            precioPromocionalAbs: computeNumericDelta(antesPrecioPromocional, despuesPrecioPromocional) ?? evento?.delta?.precioPromocionalAbs,
            precioPromocionalPct: computePercentDelta(antesPrecioPromocional, despuesPrecioPromocional) ?? evento?.delta?.precioPromocionalPct
        }
    };
};

const stabilizePeriodoMoneyValues = (periodo, summaryContext = {}) => {
    const precioPromocional = maybeFixShiftedPromotionalValue(
        periodo?.precioPromocional,
        periodo?.precioLista,
        summaryContext?.precioPromocionalActual,
        summaryContext?.precioListaActual
    );

    const precioLista = maybeFixShiftedListValue(
        periodo?.precioLista,
        precioPromocional,
        summaryContext?.precioListaActual,
        summaryContext?.precioPromocionalActual
    );

    return {
        ...periodo,
        precioLista,
        precioPromocional
    };
};

const normalizePeriodo = (periodo, index) => {
    const precioLista = toMoneyOrNull(periodo?.precioLista ?? periodo?.PrecioLista);
    const precioPromocionalInicial = toMoneyOrNull(periodo?.precioPromocional ?? periodo?.PrecioPromocional);
    const precioPromocional = maybeFixShiftedPromotionalValue(
        precioPromocionalInicial,
        precioLista,
        null
    );

    return {
        id: periodo?.id ?? periodo?.Id ?? `${periodo?.fechaDesde ?? periodo?.FechaDesde ?? 'periodo'}-${index}`,
        fechaDesde: periodo?.fechaDesde ?? periodo?.FechaDesde ?? null,
        fechaHasta: periodo?.fechaHasta ?? periodo?.FechaHasta ?? null,
        precioLista,
        precioPromocional,
        stockInicial: toNumberOrNull(periodo?.stockInicial ?? periodo?.StockInicial),
        stockFinal: toNumberOrNull(periodo?.stockFinal ?? periodo?.StockFinal),
        cantidadVentas: toNumberOrNull(periodo?.cantidadVentas ?? periodo?.CantidadVentas) ?? 0,
        idsOrdenes: normalizeOrdenesPeriodo(periodo),
        alertas: toArray(periodo?.alertas, periodo?.Alertas).map(normalizeAlert).filter(Boolean),
        cantidadEventos: toArray(periodo?.eventos, periodo?.Eventos).length,
        eventosRaw: toArray(periodo?.eventos, periodo?.Eventos)
    };
};

const resolvePeriodoDetailedData = (periodo, summaryContext = {}) => {
    const eventos = toArray(periodo?.eventosRaw).map((evento, eventIndex) => normalizeEvento(evento, eventIndex, {
        precioLista: periodo?.precioLista,
        precioPromocional: periodo?.precioPromocional
    })).map((evento) => stabilizeEventoMoneyValues(evento, {
        precioLista: periodo?.precioLista,
        precioPromocional: periodo?.precioPromocional,
        summaryPrecioLista: summaryContext?.precioListaActual,
        summaryPrecioPromocional: summaryContext?.precioPromocionalActual
    }));

    return {
        ...periodo,
        eventos
    };
};

const normalizeAuditoriaResponse = (rawData) => {
    const data = rawData?.data ?? rawData ?? {};
    const resumen = data?.resumen ?? data?.Resumen ?? {};
    const periodos = toArray(data?.periodos, data?.Periodos).map(normalizePeriodo);

    return {
        resumen: {
            precioListaActual: toMoneyOrNull(resumen?.precioListaActual ?? resumen?.PrecioListaActual),
            precioPromocionalActual: toMoneyOrNull(resumen?.precioPromocionalActual ?? resumen?.PrecioPromocionalActual),
            stockActual: toNumberOrNull(resumen?.stockActual ?? resumen?.StockActual),
            ventas90Dias: toNumberOrNull(resumen?.ventas90Dias ?? resumen?.Ventas90Dias) ?? 0,
            cantidadAlertas: toNumberOrNull(resumen?.cantidadAlertas ?? resumen?.CantidadAlertas) ?? 0
        },
        periodos
    };
};

const normalizeSellerAuditoriaEvento = (evento, index) => ({
    id: evento?.idMovimiento ?? evento?.IdMovimiento ?? `${evento?.fecha ?? evento?.Fecha ?? 'evento'}-${index}`,
    fecha: evento?.fecha ?? evento?.Fecha ?? null,
    idUsuario: toNumberOrNull(evento?.idUsuario ?? evento?.IdUsuario),
    accion: evento?.accion ?? evento?.Accion ?? '-',
    campo: evento?.campo ?? evento?.Campo ?? '-',
    valorAnterior: evento?.valorAnterior ?? evento?.ValorAnterior ?? null,
    valorActual: evento?.valorActual ?? evento?.ValorActual ?? null,
    usuario: evento?.usuario ?? evento?.Usuario ?? '-',
    observacion: evento?.observacion ?? evento?.Observacion ?? ''
});

const normalizeSellerAuditoriaPeriodo = (periodo, index) => {
    const eventos = toArray(periodo?.eventos, periodo?.Eventos).map(normalizeSellerAuditoriaEvento);
    const camposModificados = firstArray(periodo?.camposModificados, periodo?.CamposModificados)
        .map((campo) => toMeaningfulValue(campo))
        .filter(Boolean);
    const usuarios = firstArray(periodo?.usuarios, periodo?.Usuarios)
        .map((usuario) => toMeaningfulValue(usuario))
        .filter(Boolean);

    return {
        id: periodo?.id ?? periodo?.Id ?? `${periodo?.fechaDesde ?? periodo?.FechaDesde ?? 'periodo'}-${index}`,
        fechaDesde: periodo?.fechaDesde ?? periodo?.FechaDesde ?? null,
        fechaHasta: periodo?.fechaHasta ?? periodo?.FechaHasta ?? null,
        cantidadMovimientos: toNumberOrNull(periodo?.cantidadMovimientos ?? periodo?.CantidadMovimientos) ?? eventos.length,
        camposModificados,
        usuarios,
        eventos
    };
};

const normalizeSellerAuditoriaResponse = (rawData) => {
    const data = rawData?.data ?? rawData ?? {};
    const periodos = toArray(data?.periodos, data?.Periodos).map(normalizeSellerAuditoriaPeriodo);
    const resumen = data?.resumen ?? data?.Resumen ?? {};

    return {
        resumen: {
            idSeller: toNumberOrNull(resumen?.idSeller ?? resumen?.IdSeller),
            razonSocial: resumen?.razonSocial ?? resumen?.RazonSocial ?? '',
            cuit: resumen?.cuit ?? resumen?.Cuit ?? '',
            integradorActual: resumen?.integradorActual ?? resumen?.IntegradorActual ?? '',
            estadoActual: resumen?.estadoActual ?? resumen?.EstadoActual ?? '',
            usuarioActual: resumen?.usuarioActual ?? resumen?.UsuarioActual ?? '',
            emailUsuarioActual: resumen?.emailUsuarioActual ?? resumen?.EmailUsuarioActual ?? '',
            fechaDesde: resumen?.fechaDesde ?? resumen?.FechaDesde ?? null,
            fechaHasta: resumen?.fechaHasta ?? resumen?.FechaHasta ?? null,
            diasConsultados: toNumberOrNull(resumen?.diasConsultados ?? resumen?.DiasConsultados) ?? 0,
            cantidadMovimientos: toNumberOrNull(resumen?.cantidadMovimientos ?? resumen?.CantidadMovimientos) ?? periodos.reduce((acc, periodo) => acc + (periodo?.cantidadMovimientos ?? 0), 0),
            cantidadAltas: toNumberOrNull(resumen?.cantidadAltas ?? resumen?.CantidadAltas) ?? 0,
            cantidadModificaciones: toNumberOrNull(resumen?.cantidadModificaciones ?? resumen?.CantidadModificaciones) ?? 0,
            fechaUltimoMovimiento: resumen?.fechaUltimoMovimiento ?? resumen?.FechaUltimoMovimiento ?? null
        },
        periodos
    };
};

const currencyFormatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 2
});

const integerFormatter = new Intl.NumberFormat('es-AR');

const formatMoney = (value) => {
    if (value == null) return '-';
    return currencyFormatter.format(value);
};

const formatSignedMoney = (value) => {
    if (value == null) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${currencyFormatter.format(value)}`;
};

const formatInteger = (value) => {
    if (value == null) return '-';
    return integerFormatter.format(value);
};

const formatSignedInteger = (value) => {
    if (value == null) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${integerFormatter.format(value)}`;
};

const formatPercent = (value) => {
    if (value == null) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
};

const formatVariation = (numericValue, percentValue, formatter) => {
    if (numericValue == null && percentValue == null) return '-';
    return `${formatter(numericValue)} (${formatPercent(percentValue)})`;
};

const formatTextValue = (value) => {
    const normalizedValue = toMeaningfulValue(value);
    return normalizedValue == null ? '-' : String(normalizedValue);
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const getOrdenesPeriodoStatusText = (periodo, resolvedOrderIds = [], fetchStatus = null) => {
    if (resolvedOrderIds?.length) {
        return resolvedOrderIds.join(', ');
    }

    if (fetchStatus === 'loading') {
        return 'Buscando ordenes...';
    }

    return periodo.cantidadVentas > 0 ? 'No informado por API' : 'Sin ordenes';
};

const getAlertDescriptions = (alertas = []) => (
    alertas
        .map((alerta) => alerta?.descripcion)
        .filter(Boolean)
        .join(' | ')
);

const toTrimmedString = (value) => {
    if (value == null) return '';
    return String(value).trim();
};

const toMeaningfulValue = (value) => {
    if (value == null) return null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? null : trimmed;
    }
    return value;
};

const firstMeaningfulValue = (...values) => {
    for (const value of values) {
        const normalized = toMeaningfulValue(value);
        if (normalized != null) {
            return normalized;
        }
    }

    return null;
};

const resolveAuditoriaScope = (value) => {
    const normalizedScope = toTrimmedString(value).toLowerCase();
    return normalizedScope === 'seller' ? 'seller' : 'publicaciones';
};

const hasOwnData = (value) => (
    value != null
    && typeof value === 'object'
    && Object.keys(value).length > 0
);

const buildExcelFileName = ({ recordId, skuMarket, prefix = 'auditoria-precio-stock' }) => {
    const now = new Date();
    const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
        '-',
        String(now.getHours()).padStart(2, '0'),
        String(now.getMinutes()).padStart(2, '0')
    ].join('');

    const safePrefix = toTrimmedString(prefix)
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '-')
        || 'auditoria';

    const safeRecordId = String(recordId ?? skuMarket ?? 'auditoria')
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '-');

    return `${safePrefix}-${safeRecordId}-${timestamp}.xlsx`;
};

const appendWorkbookSheet = (workbook, name, rows, cols = []) => {
    const worksheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{}]);
    if (cols.length) {
        worksheet['!cols'] = cols;
    }
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
};

const renderOrdenesPeriodo = (periodo, resolvedOrderIds = [], fetchStatus = null) => {
    if (resolvedOrderIds?.length) {
        return (
            <div className="auditoria-publicacion__orders">
                {resolvedOrderIds.map((idOrden) => (
                    <span key={`${periodo.id}-${idOrden}`} className="auditoria-publicacion__order-badge">
                        {idOrden}
                    </span>
                ))}
            </div>
        );
    }

    if (fetchStatus === 'loading') {
        return <span className="auditoria-publicacion__muted">Buscando ordenes...</span>;
    }

    return periodo.cantidadVentas > 0
        ? <span className="auditoria-publicacion__muted">No informado por API</span>
        : <span className="auditoria-publicacion__muted">Sin ordenes</span>;
};

const monthsOptions = [
    { label: '30 dias', value: 1 },
    { label: '60 dias', value: 2 },
    { label: '90 dias', value: 3 }
];

const pageSizeOptions = [5, 10, 20];

const headerTooltips = {
    periodo: 'Cada fila resume un tramo verificado entre la fecha inicial y la siguiente verificacion registrada. Sirve para revisar continuidad y detectar cuando cambia el estado de la publicacion.',
    precioLista: 'Precio de lista detectado para ese tramo auditado.',
    precioPromo: 'Precio promocional detectado para ese tramo. Si llega desfasado, se corrige tomando como referencia la publicacion actual.',
    stock: 'Compara el stock al inicio y al cierre del periodo.',
    ventas: 'Cantidad de ventas registradas dentro de ese periodo.',
    ordenes: 'IDs de orden informados por la API para ese tramo. Si hubo ventas sin IDs, se aclara en la celda.',
    alertas: 'Alertas automaticas detectadas por cambios bruscos o inconsistencias dentro del periodo.'
};

const sellerHeaderTooltips = {
    periodo: 'Cada fila agrupa los movimientos del seller dentro del rango auditado.',
    movimientos: 'Cantidad total de movimientos auditados en ese periodo.',
    camposModificados: 'Campos del seller que registraron cambios dentro del periodo.',
    usuarios: 'Usuarios que participaron en modificaciones dentro del periodo.'
};

const HeaderWithTooltip = ({ label, tooltip }) => (
    <span className="auditoria-publicacion__header-label">
        <span>{label}</span>
        {tooltip ? (
            <span
                className="auditoria-publicacion__header-info"
                title={tooltip}
                aria-label={`${label}: ${tooltip}`}
                tabIndex="0"
            >
                i
            </span>
        ) : null}
    </span>
);

function AuditoriaPrecioStock({
    idPublicacion,
    registroId,
    publicacion,
    seller,
    idSeller,
    auditoriaScope = 'publicaciones',
    idTabla,
    entityDisplayName,
    excelIdLabel,
    exportFilePrefix
}) {
    const resolvedScope = resolveAuditoriaScope(auditoriaScope);
    const isSellerAudit = resolvedScope === 'seller';
    const entityData = resolvedScope === 'seller' ? seller : publicacion;
    const resolvedRecordId = isSellerAudit
        ? firstMeaningfulValue(
            entityData?.codigo,
            entityData?.Codigo,
            entityData?.idSeller,
            entityData?.IdSeller,
            registroId,
            idSeller
        )
        : firstMeaningfulValue(
            registroId,
            idPublicacion,
            entityData?.idPublicacion,
            entityData?.IdPublicacion,
            entityData?.codigo,
            entityData?.Codigo,
            entityData?.idSeller,
            entityData?.IdSeller
        );
    const resolvedEntityDisplayName = entityDisplayName ?? (resolvedScope === 'seller' ? 'seller' : 'publicacion');
    const resolvedExcelIdLabel = excelIdLabel ?? (resolvedScope === 'seller' ? 'Id seller' : 'Id publicacion');
    const resolvedExportFilePrefix = exportFilePrefix ?? (resolvedScope === 'seller' ? 'auditoria-seller' : 'auditoria-precio-stock');
    const inlineAuditoriaTableId = toTrimmedString(idTabla);
    const [meses, setMeses] = useState(1);
    const [soloAlertas, setSoloAlertas] = useState(false);
    const [soloCambiosReales, setSoloCambiosReales] = useState(() => !isSellerAudit);
    const [expandedPeriodId, setExpandedPeriodId] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [auditoriaTableId, setAuditoriaTableId] = useState(inlineAuditoriaTableId);
    const [response, setResponse] = useState(buildEmptyResponse());
    const [fallbackOrderIdsByPeriod, setFallbackOrderIdsByPeriod] = useState({});
    const [fallbackOrderFetchStatus, setFallbackOrderFetchStatus] = useState({});
    const fallbackOrderIdsByPeriodRef = useRef({});
    const fallbackOrderFetchStatusRef = useRef({});

    useEffect(() => {
        let active = true;

        const resolveTableId = async () => {
            if (isSellerAudit) {
                setAuditoriaTableId('');
                return;
            }

            if (inlineAuditoriaTableId) {
                setAuditoriaTableId(inlineAuditoriaTableId);
                return;
            }

            try {
                const configuredTableId = await getAuditoriaTableId(resolvedScope);
                if (!active) return;
                setAuditoriaTableId(toTrimmedString(configuredTableId));
            } catch {
                if (!active) return;
                setAuditoriaTableId('');
            }
        };

        resolveTableId();

        return () => {
            active = false;
        };
    }, [inlineAuditoriaTableId, isSellerAudit, resolvedScope]);

    useEffect(() => {
        let active = true;

        const fetchData = async () => {
            if (!resolvedRecordId) {
                setResponse(buildEmptyResponse());
                setLoading(false);
                setError('');
                return;
            }

            if (!isSellerAudit && !auditoriaTableId) {
                setResponse(buildEmptyResponse());
                setLoading(false);
                setError(`No se encontro configurado el IdTabla de auditoria para ${resolvedEntityDisplayName}.`);
                return;
            }

            setLoading(true);
            setError('');

            try {
                if (isSellerAudit) {
                    const data = await fetchSellerAuditoriaApi({
                        IdSeller: resolvedRecordId,
                        Dias: meses * 30
                    });

                    if (!active) return;
                    setResponse(normalizeSellerAuditoriaResponse(data));
                    return;
                }

                const requestParams = {
                    idPublicacion: resolvedRecordId,
                    IdTabla: auditoriaTableId,
                    meses,
                    soloAlertas,
                    soloCambiosReales,
                    incluirOrdenes: true,
                    incluirVentas: true,
                    incluirIdsOrdenes: true
                };

                const data = await fetchPublicacionAuditoriaPrecioStockApi(requestParams);

                if (!active) return;
                setResponse(normalizeAuditoriaResponse(data));
            } catch (err) {
                if (!active) return;

                const status = err?.response?.status;
                if (status === 404) {
                    setError(
                        resolvedScope === 'seller'
                            ? 'La auditoria todavia no esta disponible para este seller.'
                            : 'La auditoria todavia no esta disponible para esta publicacion.'
                    );
                } else {
                    setError('No se pudo cargar la auditoria.');
                }
                setResponse(buildEmptyResponse());
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            active = false;
        };
    }, [resolvedRecordId, auditoriaTableId, resolvedEntityDisplayName, resolvedScope, isSellerAudit, meses, soloAlertas, soloCambiosReales]);

    const summaryEntity = entityData ?? {};
    const sellerAuditSummary = useMemo(() => (
        isSellerAudit ? (response?.resumen ?? {}) : {}
    ), [isSellerAudit, response]);
    const resolvedSkuMarket = summaryEntity?.skuMarket ?? summaryEntity?.SkuMarket ?? null;
    const resolvedOwnerSellerId = summaryEntity?.idSeller
        ?? summaryEntity?.IdSeller
        ?? summaryEntity?.codigo
        ?? summaryEntity?.Codigo
        ?? idSeller
        ?? null;
    const sellerIntegrador = sellerAuditSummary?.integradorActual
        ?? summaryEntity?.integrador?.descripcion
        ?? summaryEntity?.integrador?.Descripcion
        ?? summaryEntity?.descripcionIntegrador
        ?? summaryEntity?.DescripcionIntegrador
        ?? summaryEntity?.integradorDescripcion
        ?? summaryEntity?.IntegradorDescripcion
        ?? '-';
    const estadoSeller = String(
        sellerAuditSummary?.estadoActual
        ?? summaryEntity?.estado
        ?? summaryEntity?.Estado
        ?? summaryEntity?.activo
        ?? summaryEntity?.Activo
        ?? ''
    ).trim().toLowerCase();
    const sellerActivo = (
        estadoSeller === 'activo'
        || estadoSeller === 'true'
        || estadoSeller === '1'
    ) ? 'Si' : 'No';
    const sellerCuit = sellerAuditSummary?.cuit
        ?? summaryEntity?.cuit
        ?? summaryEntity?.Cuit
        ?? '-';
    const sellerCantidadMovimientos = toNumberOrNull(sellerAuditSummary?.cantidadMovimientos) ?? 0;
    const sellerDiasConsultados = toNumberOrNull(sellerAuditSummary?.diasConsultados) ?? (meses * 30);
    const sellerUsuarioActual = sellerAuditSummary?.usuarioActual ?? '-';
    const sellerEmailUsuarioActual = sellerAuditSummary?.emailUsuarioActual ?? '-';
    const sellerEstadoActual = sellerAuditSummary?.estadoActual ?? summaryEntity?.estado ?? summaryEntity?.Estado ?? '-';
    const sellerRazonSocial = sellerAuditSummary?.razonSocial ?? summaryEntity?.razonSocial ?? summaryEntity?.RazonSocial ?? '-';
    const resumen = useMemo(() => ({
        precioListaActual: response?.resumen?.precioListaActual ?? toMoneyOrNull(summaryEntity?.precioLista),
        precioPromocionalActual: response?.resumen?.precioPromocionalActual ?? toMoneyOrNull(summaryEntity?.precioPromocional),
        stockActual: response?.resumen?.stockActual ?? toNumberOrNull(summaryEntity?.stock),
        ventas90Dias: response?.resumen?.ventas90Dias ?? 0,
        cantidadAlertas: response?.resumen?.cantidadAlertas ?? 0
    }), [response, summaryEntity]);
    const secondaryAuditRow = useMemo(() => (
        isSellerAudit
            ? { label: 'Integrador', value: sellerIntegrador || '-' }
            : { label: 'Sku market', value: resolvedSkuMarket ?? '-' }
    ), [isSellerAudit, sellerIntegrador, resolvedSkuMarket]);
    const tertiaryAuditRow = useMemo(() => (
        isSellerAudit
            ? { label: 'CUIT', value: sellerCuit }
            : { label: 'Seller', value: resolvedOwnerSellerId ?? '-' }
    ), [isSellerAudit, resolvedOwnerSellerId, sellerCuit]);
    const quaternaryAuditRow = useMemo(() => (
        isSellerAudit
            ? { label: 'Cantidad de movimientos', value: formatInteger(sellerCantidadMovimientos) }
            : null
    ), [isSellerAudit, sellerCantidadMovimientos]);
    const summaryCards = useMemo(() => (
        isSellerAudit
            ? [
                { label: 'Activo', value: sellerActivo },
                { label: 'Integrador', value: sellerIntegrador || '-' },
                { label: 'CUIT', value: sellerCuit },
                { label: 'Cantidad movimientos', value: formatInteger(sellerCantidadMovimientos) }
            ]
            : [
                { label: 'Precio lista actual', value: formatMoney(resumen.precioListaActual) },
                { label: 'Precio promo actual', value: formatMoney(resumen.precioPromocionalActual) },
                { label: 'Stock actual', value: formatInteger(resumen.stockActual) },
                { label: 'Ventas del periodo', value: formatInteger(resumen.ventas90Dias) },
                { label: 'Alertas activas', value: formatInteger(resumen.cantidadAlertas), alert: true }
            ]
    ), [isSellerAudit, sellerActivo, sellerCantidadMovimientos, sellerCuit, sellerIntegrador, resumen]);

    const periodos = useMemo(() => (
        isSellerAudit
            ? toArray(response?.periodos)
            : toArray(response?.periodos).map((periodo) => stabilizePeriodoMoneyValues(periodo, resumen))
    ), [response, resumen, isSellerAudit]);
    const hasLoadingOrderLookups = useMemo(() => (
        Object.values(fallbackOrderFetchStatus).some((status) => status === 'loading')
    ), [fallbackOrderFetchStatus]);
    const pageCount = Math.ceil(periodos.length / pageSize);
    const safeCurrentPage = pageCount > 0 ? Math.min(currentPage, pageCount - 1) : 0;
    const periodosPaginados = useMemo(() => {
        const startIndex = safeCurrentPage * pageSize;
        return periodos.slice(startIndex, startIndex + pageSize);
    }, [periodos, pageSize, safeCurrentPage]);
    const expandedPeriodoDetalle = useMemo(() => {
        const periodo = periodosPaginados.find((currentPeriodo) => currentPeriodo.id === expandedPeriodId);
        if (!periodo) return null;
        return isSellerAudit ? periodo : resolvePeriodoDetailedData(periodo, resumen);
    }, [periodosPaginados, expandedPeriodId, isSellerAudit, resumen]);
    const firstVisibleResult = periodos.length > 0 ? (safeCurrentPage * pageSize) + 1 : 0;
    const lastVisibleResult = Math.min((safeCurrentPage + 1) * pageSize, periodos.length);

    useEffect(() => {
        setCurrentPage(0);
        setExpandedPeriodId(null);
    }, [resolvedRecordId, meses, soloAlertas, soloCambiosReales, pageSize]);

    useEffect(() => {
        setFallbackOrderIdsByPeriod({});
        setFallbackOrderFetchStatus({});
    }, [resolvedRecordId, meses, soloAlertas, soloCambiosReales]);

    useEffect(() => {
        fallbackOrderIdsByPeriodRef.current = fallbackOrderIdsByPeriod;
    }, [fallbackOrderIdsByPeriod]);

    useEffect(() => {
        fallbackOrderFetchStatusRef.current = fallbackOrderFetchStatus;
    }, [fallbackOrderFetchStatus]);

    useEffect(() => {
        if (currentPage !== safeCurrentPage) {
            setCurrentPage(safeCurrentPage);
            setExpandedPeriodId(null);
        }
    }, [currentPage, safeCurrentPage]);

    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
        setExpandedPeriodId(null);
    };

    useEffect(() => {
        const currentFetchStatus = fallbackOrderFetchStatusRef.current;
        const periodosParaResolver = buildPeriodosToResolve(periodosPaginados, currentFetchStatus, resolvedSkuMarket);

        if (!periodosParaResolver.length) return undefined;

        let active = true;

        setFallbackOrderFetchStatus((prev) => (
            periodosParaResolver.reduce((acc, periodo) => ({
                ...acc,
                [periodo.id]: 'loading'
            }), prev)
        ));

        resolveOrderIdsForPeriodos({
            periodos: periodosParaResolver,
            skuMarket: resolvedSkuMarket,
            sellerId: resolvedOwnerSellerId
        }).then((resultsByPeriod) => {
            if (!active) return;

            setFallbackOrderIdsByPeriod((prev) => (
                Object.entries(resultsByPeriod).reduce((acc, [periodId, idsOrdenes]) => ({
                    ...acc,
                    [periodId]: idsOrdenes
                }), prev)
            ));

            setFallbackOrderFetchStatus((prev) => (
                Object.entries(resultsByPeriod).reduce((acc, [periodId, idsOrdenes]) => ({
                    ...acc,
                    [periodId]: idsOrdenes.length ? 'resolved' : 'empty'
                }), prev)
            ));
        });

        return () => {
            active = false;
        };
    }, [periodosPaginados, resolvedOwnerSellerId, resolvedSkuMarket]);

    const getPeriodoOrderIds = (periodo) => uniqueValues([
        ...toArray(periodo?.idsOrdenes),
        ...toArray(fallbackOrderIdsByPeriod[periodo.id])
    ]);

    const handleExportExcel = async () => {
        if (!periodos.length || exporting) return;

        if (isSellerAudit) {
            setExporting(true);

            try {
                const workbook = XLSX.utils.book_new();
                const totalEventos = periodos.reduce((acc, periodo) => acc + (periodo?.eventos?.length ?? 0), 0);
                const auditoriaRows = [
                    { Dato: 'Id seller', Valor: sellerAuditSummary?.idSeller ?? resolvedRecordId ?? '-' },
                    { Dato: 'Razon social', Valor: sellerRazonSocial },
                    { Dato: 'CUIT', Valor: sellerCuit },
                    { Dato: 'Integrador actual', Valor: sellerIntegrador || '-' },
                    { Dato: 'Estado actual', Valor: sellerEstadoActual },
                    { Dato: 'Usuario actual', Valor: sellerUsuarioActual },
                    { Dato: 'Email usuario actual', Valor: sellerEmailUsuarioActual },
                    { Dato: 'Fecha desde', Valor: formatDate(sellerAuditSummary?.fechaDesde) },
                    { Dato: 'Fecha hasta', Valor: formatDate(sellerAuditSummary?.fechaHasta) },
                    { Dato: 'Dias consultados', Valor: sellerDiasConsultados },
                    { Dato: 'Cantidad movimientos', Valor: sellerCantidadMovimientos },
                    { Dato: 'Cantidad altas', Valor: toNumberOrNull(sellerAuditSummary?.cantidadAltas) ?? 0 },
                    { Dato: 'Cantidad modificaciones', Valor: toNumberOrNull(sellerAuditSummary?.cantidadModificaciones) ?? 0 },
                    { Dato: 'Fecha ultimo movimiento', Valor: formatDate(sellerAuditSummary?.fechaUltimoMovimiento) },
                    { Dato: 'Cantidad de periodos exportados', Valor: periodos.length },
                    { Dato: 'Cantidad de eventos exportados', Valor: totalEventos },
                    { Dato: 'Exportado el', Valor: formatDate(new Date()) }
                ];

                const periodRows = periodos.map((periodo) => ({
                    'Periodo ID': periodo.id,
                    'Fecha desde': formatDate(periodo.fechaDesde),
                    'Fecha hasta': formatDate(periodo.fechaHasta),
                    'Cantidad movimientos': periodo.cantidadMovimientos ?? 0,
                    'Campos modificados': periodo.camposModificados?.join(' | ') || '-',
                    'Usuarios': periodo.usuarios?.join(' | ') || '-',
                    'Cantidad eventos': periodo.eventos?.length ?? 0
                }));

                const detailRows = periodos.flatMap((periodo) => {
                    if (!periodo.eventos?.length) {
                        return [{
                            'Periodo ID': periodo.id,
                            'Fecha desde periodo': formatDate(periodo.fechaDesde),
                            'Fecha hasta periodo': formatDate(periodo.fechaHasta),
                            'Fecha evento': '',
                            'Accion': '',
                            'Campo': '',
                            'Usuario': '',
                            'Id usuario': '',
                            'Valor anterior': '',
                            'Valor actual': '',
                            'Observacion': 'Periodo sin eventos detallados'
                        }];
                    }

                    return periodo.eventos.map((evento) => ({
                        'Periodo ID': periodo.id,
                        'Fecha desde periodo': formatDate(periodo.fechaDesde),
                        'Fecha hasta periodo': formatDate(periodo.fechaHasta),
                        'Fecha evento': formatDate(evento.fecha),
                        'Accion': formatTextValue(evento.accion),
                        'Campo': formatTextValue(evento.campo),
                        'Usuario': formatTextValue(evento.usuario),
                        'Id usuario': evento.idUsuario ?? '',
                        'Valor anterior': formatTextValue(evento.valorAnterior),
                        'Valor actual': formatTextValue(evento.valorActual),
                        'Observacion': formatTextValue(evento.observacion)
                    }));
                });

                appendWorkbookSheet(workbook, 'Auditoria', auditoriaRows, [
                    { wch: 28 },
                    { wch: 36 }
                ]);
                appendWorkbookSheet(workbook, 'Periodos', periodRows, [
                    { wch: 18 },
                    { wch: 22 },
                    { wch: 22 },
                    { wch: 20 },
                    { wch: 40 },
                    { wch: 28 },
                    { wch: 18 }
                ]);
                appendWorkbookSheet(workbook, 'Detalle periodos', detailRows, [
                    { wch: 18 },
                    { wch: 22 },
                    { wch: 22 },
                    { wch: 22 },
                    { wch: 18 },
                    { wch: 28 },
                    { wch: 28 },
                    { wch: 14 },
                    { wch: 28 },
                    { wch: 28 },
                    { wch: 70 }
                ]);

                XLSX.writeFile(workbook, buildExcelFileName({
                    recordId: resolvedRecordId,
                    prefix: resolvedExportFilePrefix
                }));
            } finally {
                setExporting(false);
            }

            return;
        }

        const currentFetchStatus = fallbackOrderFetchStatusRef.current;
        const periodosParaResolver = buildPeriodosToResolve(periodos, currentFetchStatus, resolvedSkuMarket);

        setExporting(true);

        try {
            let resolvedFallbackIds = {};

            if (periodosParaResolver.length) {
                setFallbackOrderFetchStatus((prev) => (
                    periodosParaResolver.reduce((acc, periodo) => ({
                        ...acc,
                        [periodo.id]: 'loading'
                    }), prev)
                ));

                resolvedFallbackIds = await resolveOrderIdsForPeriodos({
                    periodos: periodosParaResolver,
                    skuMarket: resolvedSkuMarket,
                    sellerId: resolvedOwnerSellerId
                });

                setFallbackOrderIdsByPeriod((prev) => ({
                    ...prev,
                    ...resolvedFallbackIds
                }));

                setFallbackOrderFetchStatus((prev) => (
                    Object.entries(resolvedFallbackIds).reduce((acc, [periodId, idsOrdenes]) => ({
                        ...acc,
                        [periodId]: idsOrdenes.length ? 'resolved' : 'empty'
                    }), prev)
                ));
            }

            const mergedFallbackIds = {
                ...fallbackOrderIdsByPeriodRef.current,
                ...resolvedFallbackIds
            };
            const detailedPeriodos = periodos.map((periodo) => resolvePeriodoDetailedData(periodo, resumen));

            const workbook = XLSX.utils.book_new();
            const totalEventos = detailedPeriodos.reduce((acc, periodo) => acc + (periodo?.eventos?.length ?? 0), 0);
            const auditoriaRows = [
                { Dato: resolvedExcelIdLabel, Valor: resolvedRecordId ?? '-' },
                { Dato: secondaryAuditRow.label, Valor: secondaryAuditRow.value },
                { Dato: tertiaryAuditRow.label, Valor: tertiaryAuditRow.value },
                ...(quaternaryAuditRow ? [{ Dato: quaternaryAuditRow.label, Valor: quaternaryAuditRow.value }] : []),
                ...(isSellerAudit
                    ? [{ Dato: 'Activo', Valor: sellerActivo }]
                    : [
                        { Dato: 'Precio lista actual', Valor: resumen.precioListaActual },
                        { Dato: 'Precio promo actual', Valor: resumen.precioPromocionalActual },
                        { Dato: 'Stock actual', Valor: resumen.stockActual },
                        { Dato: 'Ventas totales auditadas', Valor: resumen.ventas90Dias },
                        { Dato: 'Alertas activas', Valor: resumen.cantidadAlertas }
                    ]),
                { Dato: 'Cantidad de periodos exportados', Valor: periodos.length },
                { Dato: 'Cantidad de eventos exportados', Valor: totalEventos },
                { Dato: 'Exportado el', Valor: formatDate(new Date()) }
            ];

            const periodRows = periodos.map((periodo) => {
                const orderIds = uniqueValues([
                    ...toArray(periodo?.idsOrdenes),
                    ...toArray(mergedFallbackIds[periodo.id])
                ]);
                const orderStatus = getOrdenesPeriodoStatusText(
                    periodo,
                    orderIds,
                    fallbackOrderFetchStatusRef.current[periodo.id] ?? null
                );

                return {
                    'Periodo ID': periodo.id,
                    'Fecha desde': formatDate(periodo.fechaDesde),
                    'Fecha hasta': formatDate(periodo.fechaHasta),
                    'Precio lista': periodo.precioLista,
                    'Precio promo': periodo.precioPromocional,
                    'Stock inicial': periodo.stockInicial,
                    'Stock final': periodo.stockFinal,
                    'Ventas': periodo.cantidadVentas,
                    'Ordenes': orderStatus,
                    'Cantidad ordenes': orderIds.length,
                    'Alertas': getAlertDescriptions(periodo.alertas),
                    'Cantidad alertas': periodo.alertas?.length ?? 0,
                    'Cantidad eventos': periodo.cantidadEventos ?? 0
                };
            });

            const detailRows = detailedPeriodos.flatMap((periodo) => {
                const orderIds = uniqueValues([
                    ...toArray(periodo?.idsOrdenes),
                    ...toArray(mergedFallbackIds[periodo.id])
                ]);
                const orderStatus = getOrdenesPeriodoStatusText(
                    periodo,
                    orderIds,
                    fallbackOrderFetchStatusRef.current[periodo.id] ?? null
                );

                if (!periodo.eventos?.length) {
                    return [{
                        'Periodo ID': periodo.id,
                        'Fecha desde periodo': formatDate(periodo.fechaDesde),
                        'Fecha hasta periodo': formatDate(periodo.fechaHasta),
                        'Precio lista periodo': periodo.precioLista,
                        'Precio promo periodo': periodo.precioPromocional,
                        'Stock inicial periodo': periodo.stockInicial,
                        'Stock final periodo': periodo.stockFinal,
                        'Ventas periodo': periodo.cantidadVentas,
                        'Ordenes periodo': orderStatus,
                        'Alertas periodo': getAlertDescriptions(periodo.alertas),
                        'Fecha evento': '',
                        'Origen': '',
                        'Usuario': '',
                        'Id usuario': '',
                        'Antes lista': '',
                        'Antes promo': '',
                        'Antes stock': '',
                        'Despues lista': '',
                        'Despues promo': '',
                        'Despues stock': '',
                        'Delta lista abs': '',
                        'Delta lista pct': '',
                        'Delta promo abs': '',
                        'Delta promo pct': '',
                        'Delta stock abs': '',
                        'Delta stock pct': '',
                        'Alertas evento': '',
                        'Observacion': 'Periodo sin eventos detallados'
                    }];
                }

                return periodo.eventos.map((evento) => ({
                    'Periodo ID': periodo.id,
                    'Fecha desde periodo': formatDate(periodo.fechaDesde),
                    'Fecha hasta periodo': formatDate(periodo.fechaHasta),
                    'Precio lista periodo': periodo.precioLista,
                    'Precio promo periodo': periodo.precioPromocional,
                    'Stock inicial periodo': periodo.stockInicial,
                    'Stock final periodo': periodo.stockFinal,
                    'Ventas periodo': periodo.cantidadVentas,
                    'Ordenes periodo': orderStatus,
                    'Alertas periodo': getAlertDescriptions(periodo.alertas),
                    'Fecha evento': formatDate(evento.fecha),
                    'Origen': evento.origen,
                    'Usuario': evento.usuario,
                    'Id usuario': evento.idUsuario,
                    'Antes lista': evento.antes.precioLista,
                    'Antes promo': evento.antes.precioPromocional,
                    'Antes stock': evento.antes.stock,
                    'Despues lista': evento.despues.precioLista,
                    'Despues promo': evento.despues.precioPromocional,
                    'Despues stock': evento.despues.stock,
                    'Delta lista abs': evento.delta.precioListaAbs,
                    'Delta lista pct': evento.delta.precioListaPct,
                    'Delta promo abs': evento.delta.precioPromocionalAbs,
                    'Delta promo pct': evento.delta.precioPromocionalPct,
                    'Delta stock abs': evento.delta.stockAbs,
                    'Delta stock pct': evento.delta.stockPct,
                    'Alertas evento': getAlertDescriptions(evento.alertas),
                    'Observacion': ''
                }));
            });

            appendWorkbookSheet(workbook, 'Auditoria', auditoriaRows, [
                { wch: 24 },
                { wch: 30 }
            ]);
            appendWorkbookSheet(workbook, 'Periodos', periodRows, [
                { wch: 18 },
                { wch: 22 },
                { wch: 22 },
                { wch: 16 },
                { wch: 18 },
                { wch: 12 },
                { wch: 12 },
                { wch: 12 },
                { wch: 34 },
                { wch: 14 },
                { wch: 28 },
                { wch: 14 },
                { wch: 14 }
            ]);
            appendWorkbookSheet(workbook, 'Detalle periodos', detailRows, [
                { wch: 18 },
                { wch: 22 },
                { wch: 22 },
                { wch: 16 },
                { wch: 18 },
                { wch: 14 },
                { wch: 14 },
                { wch: 12 },
                { wch: 34 },
                { wch: 28 },
                { wch: 22 },
                { wch: 16 },
                { wch: 18 },
                { wch: 12 },
                { wch: 16 },
                { wch: 18 },
                { wch: 12 },
                { wch: 16 },
                { wch: 18 },
                { wch: 12 },
                { wch: 16 },
                { wch: 16 },
                { wch: 18 },
                { wch: 18 },
                { wch: 12 },
                { wch: 12 },
                { wch: 28 },
                { wch: 28 }
            ]);

            XLSX.writeFile(workbook, buildExcelFileName({
                recordId: resolvedRecordId,
                skuMarket: resolvedSkuMarket,
                prefix: resolvedExportFilePrefix
            }));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="auditoria-publicacion">
            <div className="auditoria-publicacion__summary">
                {summaryCards.map((card) => (
                    <div
                        key={card.label}
                        className={`auditoria-publicacion__card ${card.alert ? 'auditoria-publicacion__card--alert' : ''}`.trim()}
                    >
                        <span className="auditoria-publicacion__label">{card.label}</span>
                        <strong>{card.value}</strong>
                    </div>
                ))}
            </div>

            <div className="auditoria-publicacion__toolbar">
                <div className="auditoria-publicacion__months">
                    {monthsOptions.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`auditoria-publicacion__filter-btn ${meses === option.value ? 'is-active' : ''}`}
                            onClick={() => setMeses(option.value)}
                            title={`Verifica y agrupa cambios auditados de los ultimos ${option.label}.`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                {!isSellerAudit && (
                    <>
                        <label
                            className="auditoria-publicacion__checkbox"
                            title="Si esta activo, se muestran solo periodos con cambios reales de precio o stock para facilitar la verificacion."
                        >
                            <input
                                type="checkbox"
                                checked={soloCambiosReales}
                                onChange={(event) => setSoloCambiosReales(event.target.checked)}
                            />
                            <span>Ocultar actualizaciones sin cambio real</span>
                        </label>
                        <label
                            className="auditoria-publicacion__checkbox"
                            title="Muestra solo los periodos en los que la auditoria detecto alertas automaticas."
                        >
                            <input
                                type="checkbox"
                                checked={soloAlertas}
                                onChange={(event) => setSoloAlertas(event.target.checked)}
                            />
                            <span>Solo alertas</span>
                        </label>
                    </>
                )}
                <button
                    type="button"
                    className="auditoria-publicacion__export-btn"
                    onClick={handleExportExcel}
                    disabled={loading || exporting || hasLoadingOrderLookups || !!error || !periodos.length}
                    title="Descarga un Excel con resumen, periodos y eventos detallados de la auditoria."
                >
                    {exporting ? 'Exportando...' : 'Exportar Excel'}
                </button>
            </div>

            <div className="auditoria-publicacion__hint">
                {isSellerAudit
                    ? `Se muestran ${sellerDiasConsultados} dias del historial auditado del seller.`
                    : 'Se muestran hasta 3 meses de cambios y se omiten eventos sin variacion real de precio o stock.'}
            </div>

            {loading && (
                <div className="auditoria-publicacion__state">
                    Cargando auditoria...
                </div>
            )}

            {!loading && error && (
                <div className="auditoria-publicacion__state auditoria-publicacion__state--error">
                    {error}
                </div>
            )}

            {!loading && !error && periodos.length === 0 && (
                <div className="auditoria-publicacion__state">
                    {isSellerAudit
                        ? 'No hay movimientos auditables para el rango seleccionado.'
                        : 'No hay cambios auditables para el periodo seleccionado.'}
                </div>
            )}

            {!loading && !error && periodos.length > 0 && (
                <>
                    <div className="auditoria-publicacion__pagination-summary">
                        <span>
                            Mostrando {firstVisibleResult}-{lastVisibleResult} de {periodos.length} resultados
                        </span>
                        <label className="auditoria-publicacion__page-size">
                            <span>Resultados por pagina</span>
                            <select
                                value={pageSize}
                                onChange={(event) => setPageSize(Number(event.target.value))}
                            >
                                {pageSizeOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="auditoria-publicacion__table-wrap">
                        {isSellerAudit ? (
                            <table className="table auditoria-publicacion__table">
                                <thead>
                                    <tr>
                                        <th scope="col"><HeaderWithTooltip label="Periodo" tooltip={sellerHeaderTooltips.periodo} /></th>
                                        <th scope="col"><HeaderWithTooltip label="Movimientos" tooltip={sellerHeaderTooltips.movimientos} /></th>
                                        <th scope="col"><HeaderWithTooltip label="Campos modificados" tooltip={sellerHeaderTooltips.camposModificados} /></th>
                                        <th scope="col"><HeaderWithTooltip label="Usuarios" tooltip={sellerHeaderTooltips.usuarios} /></th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {periodosPaginados.map((periodo) => {
                                        const expanded = expandedPeriodId === periodo.id;
                                        const periodoDetalle = expanded && expandedPeriodoDetalle?.id === periodo.id
                                            ? expandedPeriodoDetalle
                                            : null;

                                        return (
                                            <React.Fragment key={periodo.id}>
                                                <tr>
                                                    <td>
                                                        <div className="auditoria-publicacion__period">
                                                            <strong>{formatDate(periodo.fechaDesde)}</strong>
                                                            <span>hasta {formatDate(periodo.fechaHasta)}</span>
                                                        </div>
                                                    </td>
                                                    <td>{formatInteger(periodo.cantidadMovimientos)}</td>
                                                    <td>
                                                        <div className="auditoria-publicacion__alerts">
                                                            {periodo.camposModificados?.length
                                                                ? periodo.camposModificados.map((campo) => (
                                                                    <span key={`${periodo.id}-${campo}`} className="auditoria-publicacion__badge">
                                                                        {campo}
                                                                    </span>
                                                                ))
                                                                : <span className="auditoria-publicacion__muted">Sin cambios identificados</span>}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="auditoria-publicacion__orders">
                                                            {periodo.usuarios?.length
                                                                ? periodo.usuarios.map((usuario) => (
                                                                    <span key={`${periodo.id}-${usuario}`} className="auditoria-publicacion__order-badge">
                                                                        {usuario}
                                                                    </span>
                                                                ))
                                                                : <span className="auditoria-publicacion__muted">Sin usuario informado</span>}
                                                        </div>
                                                    </td>
                                                    <td className="text-right">
                                                        <button
                                                            type="button"
                                                            className="auditoria-publicacion__toggle"
                                                            onClick={() => setExpandedPeriodId(expanded ? null : periodo.id)}
                                                        >
                                                            {expanded ? 'Ocultar detalle' : 'Ver detalle'}
                                                        </button>
                                                    </td>
                                                </tr>
                                                {expanded && (
                                                    <tr className="auditoria-publicacion__detail-row">
                                                        <td colSpan="5">
                                                            {periodoDetalle?.eventos?.length ? (
                                                                <table className="table auditoria-publicacion__events-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Fecha</th>
                                                                            <th>Accion</th>
                                                                            <th>Campo</th>
                                                                            <th>Usuario</th>
                                                                            <th>Antes</th>
                                                                            <th>Despues</th>
                                                                            <th>Observacion</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {periodoDetalle.eventos.map((evento) => (
                                                                            <tr key={evento.id}>
                                                                                <td>{formatDate(evento.fecha)}</td>
                                                                                <td>{formatTextValue(evento.accion)}</td>
                                                                                <td>{formatTextValue(evento.campo)}</td>
                                                                                <td>
                                                                                    {formatTextValue(evento.usuario)}
                                                                                    {evento.idUsuario != null ? ` (#${evento.idUsuario})` : ''}
                                                                                </td>
                                                                                <td>{formatTextValue(evento.valorAnterior)}</td>
                                                                                <td>{formatTextValue(evento.valorActual)}</td>
                                                                                <td>{formatTextValue(evento.observacion)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : (
                                                                <div className="auditoria-publicacion__muted">
                                                                    Este periodo no trajo eventos detallados.
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                        <table className="table auditoria-publicacion__table">
                        <thead>
                            <tr>
                                <th scope="col"><HeaderWithTooltip label="Periodo" tooltip={headerTooltips.periodo} /></th>
                                <th scope="col"><HeaderWithTooltip label="Precio lista" tooltip={headerTooltips.precioLista} /></th>
                                <th scope="col"><HeaderWithTooltip label="Precio promo" tooltip={headerTooltips.precioPromo} /></th>
                                <th scope="col"><HeaderWithTooltip label="Stock" tooltip={headerTooltips.stock} /></th>
                                <th scope="col"><HeaderWithTooltip label="Ventas" tooltip={headerTooltips.ventas} /></th>
                                <th scope="col"><HeaderWithTooltip label="Ordenes" tooltip={headerTooltips.ordenes} /></th>
                                <th scope="col"><HeaderWithTooltip label="Alertas" tooltip={headerTooltips.alertas} /></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {periodosPaginados.map((periodo) => {
                                const expanded = expandedPeriodId === periodo.id;
                                const orderIds = getPeriodoOrderIds(periodo);
                                const orderFetchStatus = fallbackOrderFetchStatus[periodo.id] ?? null;
                                const periodoDetalle = expanded && expandedPeriodoDetalle?.id === periodo.id
                                    ? expandedPeriodoDetalle
                                    : null;
                                return (
                                    <React.Fragment key={periodo.id}>
                                        <tr>
                                            <td>
                                                <div className="auditoria-publicacion__period">
                                                    <strong>{formatDate(periodo.fechaDesde)}</strong>
                                                    <span>hasta {formatDate(periodo.fechaHasta)}</span>
                                                </div>
                                            </td>
                                            <td>{formatMoney(periodo.precioLista)}</td>
                                            <td>{formatMoney(periodo.precioPromocional)}</td>
                                            <td>
                                                <div className="auditoria-publicacion__stock-cell">
                                                    <span>Inicio: {formatInteger(periodo.stockInicial)}</span>
                                                    <span>Cierre: {formatInteger(periodo.stockFinal)}</span>
                                                </div>
                                            </td>
                                            <td>{formatInteger(periodo.cantidadVentas)}</td>
                                            <td>{renderOrdenesPeriodo(periodo, orderIds, orderFetchStatus)}</td>
                                            <td>
                                                <div className="auditoria-publicacion__alerts">
                                                    {periodo.alertas?.length
                                                        ? periodo.alertas.map((alerta) => (
                                                            <span key={`${periodo.id}-${alerta.codigo}`} className="auditoria-publicacion__badge">
                                                                {alerta.descripcion}
                                                            </span>
                                                        ))
                                                        : <span className="auditoria-publicacion__muted">Sin alertas</span>}
                                                </div>
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    type="button"
                                                    className="auditoria-publicacion__toggle"
                                                    onClick={() => setExpandedPeriodId(expanded ? null : periodo.id)}
                                                >
                                                    {expanded ? 'Ocultar detalle' : 'Ver detalle'}
                                                </button>
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr className="auditoria-publicacion__detail-row">
                                                <td colSpan="8">
                                                    {periodoDetalle?.eventos?.length ? (
                                                        <table className="table auditoria-publicacion__events-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Fecha</th>
                                                                    <th>Origen</th>
                                                                    <th>Usuario</th>
                                                                    <th>Antes</th>
                                                                    <th>Despues</th>
                                                                    <th>Variaci&oacute;n</th>
                                                                    <th>Alertas</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {periodoDetalle.eventos.map((evento) => (
                                                                    <tr key={evento.id}>
                                                                        <td>{formatDate(evento.fecha)}</td>
                                                                        <td>{evento.origen}</td>
                                                                        <td>{evento.usuario}{evento.idUsuario ? ` (#${evento.idUsuario})` : ''}</td>
                                                                        <td>
                                                                            <div>Lista: {formatMoney(evento.antes.precioLista)}</div>
                                                                            <div>Promo: {formatMoney(evento.antes.precioPromocional)}</div>
                                                                            <div>Stock: {formatInteger(evento.antes.stock)}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div>Lista: {formatMoney(evento.despues.precioLista)}</div>
                                                                            <div>Promo: {formatMoney(evento.despues.precioPromocional)}</div>
                                                                            <div>Stock: {formatInteger(evento.despues.stock)}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="auditoria-publicacion__variation-line">
                                                                                <span>Lista:</span>
                                                                                <strong>{formatVariation(evento.delta.precioListaAbs, evento.delta.precioListaPct, formatSignedMoney)}</strong>
                                                                            </div>
                                                                            <div className="auditoria-publicacion__variation-line">
                                                                                <span>Promo:</span>
                                                                                <strong>{formatVariation(evento.delta.precioPromocionalAbs, evento.delta.precioPromocionalPct, formatSignedMoney)}</strong>
                                                                            </div>
                                                                            <div className="auditoria-publicacion__variation-line">
                                                                                <span>Stock:</span>
                                                                                <strong>{formatVariation(evento.delta.stockAbs, evento.delta.stockPct, formatSignedInteger)}</strong>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="auditoria-publicacion__alerts">
                                                                                {evento.alertas?.length
                                                                                    ? evento.alertas.map((alerta) => (
                                                                                        <span key={`${evento.id}-${alerta.codigo}`} className="auditoria-publicacion__badge">
                                                                                            {alerta.descripcion}
                                                                                        </span>
                                                                                    ))
                                                                                    : <span className="auditoria-publicacion__muted">-</span>}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <div className="auditoria-publicacion__muted">
                                                            Este periodo no trajo eventos detallados.
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                        )}
                </div>

                    {pageCount > 1 && (
                        <ReactPaginate
                            previousLabel={"Anterior"}
                            nextLabel={"Siguiente"}
                            breakLabel={"..."}
                            pageCount={pageCount}
                            marginPagesDisplayed={1}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination auditoria-publicacion__pagination"}
                            activeClassName={"active"}
                            forcePage={safeCurrentPage}
                        />
                    )}
                </>
            )}
        </div>
    );
}

const mapStateToProps = (state, ownProps) => {
    const storePublicacion = state?.publicaciones?.publicacion ?? {};
    const storeSeller = selectSellerMerged(state) ?? {};

    return {
        publicacion: hasOwnData(storePublicacion) ? storePublicacion : (ownProps.publicacion ?? {}),
        seller: hasOwnData(storeSeller) ? storeSeller : (ownProps.seller ?? {}),
        idSeller: ownProps.idSeller ?? state?.general?.idSeller ?? null
    };
};

export default connect(mapStateToProps)(AuditoriaPrecioStock);
